import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { User, Building2, Stethoscope, Clock, ExternalLink } from 'lucide-react';
import { programRoutes } from '@/modules/shared/constants/routes';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/modules/shared/components/ui/sheet';
import { Button, ButtonWithIcon } from '@/modules/shared/components/ui/button';
import { StatusBadge } from '@/modules/shared/components/ui/StatusBadge';
import { AppSkeleton } from '@/modules/shared/components/ui/AppSkeleton';
import { patientService, PatientDto } from '@/modules/programs/services/patientService';
import { normalizePatientDto } from '@/modules/shared/utils/entityNormalizer';
import { formatWorkflowAction } from '@/modules/shared/utils/formatWorkflowAction';

/**
 * PatientQuickView – Slide-out drawer for patient quick info
 *
 * Global UI Design System:
 * - Accessible from any patient reference in the app
 * - Shows: Name, Hospital, Doctor, Status, Last activity
 * - Avoids forcing navigation to patient page for basic info
 *
 * Usage:
 *   <PatientQuickView
 *     isOpen={isOpen}
 *     onClose={() => setIsOpen(false)}
 *     patientId={patientId}
 *     programId={programId}
 *   />
 */

interface PatientQuickViewProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string | null;
  programId: string;
}

export function PatientQuickView({
  isOpen,
  onClose,
  patientId,
  programId,
}: PatientQuickViewProps) {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['patient-quick-view', programId, patientId],
    queryFn: async () => {
      if (!patientId) return null;
      const response = await patientService.getPatientById(programId, patientId);
      return response.data;
    },
    enabled: isOpen && !!patientId,
    staleTime: 30000, // 30 seconds
  });

  const patient = data as PatientDto | null;
  const normalized = normalizePatientDto(patient);

  const handleViewFullProfile = () => {
    if (patientId) {
      if (patient?.hospitalId && patient?.hcpId) {
        navigate(programRoutes.patientDetail(programId, patient.hospitalId, patient.hcpId, patientId));
      } else {
        navigate(programRoutes.patients(programId));
      }
      onClose();
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-[400px] sm:w-[450px] p-0">
        <SheetHeader className="p-4 border-b border-border">
          <SheetTitle className="text-[14px] font-semibold">Patient Quick View</SheetTitle>
        </SheetHeader>

        <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-140px)]">
          {isLoading && <PatientQuickViewSkeleton />}

          {error && (
            <div className="text-center py-8 text-destructive text-[12px]">
              Failed to load patient information
            </div>
          )}

          {!isLoading && !error && patient && (
            <>
              {/* Patient Header */}
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-primary-soft flex items-center justify-center shrink-0">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-[14px] font-semibold text-foreground truncate">
                    {normalized.fullName}
                  </h3>
                  <p className="text-[12px] text-muted-foreground">
                    {normalized.patientCode}
                  </p>
                  <div className="mt-1.5">
                    <StatusBadge status={patient.status} />
                  </div>
                </div>
              </div>

              {/* Info Grid */}
              <div className="space-y-3 pt-2">
                {/* Hospital */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Hospital</p>
                    <p className="text-[12px] font-medium text-foreground truncate">
                      {normalized.hospitalName}
                    </p>
                  </div>
                </div>

                {/* Doctor/HCP */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <Stethoscope className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Doctor</p>
                    <p className="text-[12px] font-medium text-foreground truncate">
                      {normalized.hcpName}
                    </p>
                  </div>
                </div>

                {/* Workflow Step */}
                {patient.currentWorkflowStepName && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Workflow Step</p>
                      <p className="text-[12px] font-medium text-foreground truncate">
                        {formatWorkflowAction(patient.currentWorkflowStepName)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Last Activity */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Last Updated</p>
                    <p className="text-[12px] font-medium text-foreground">
                      {formatDate(patient.updatedAt || patient.createdAt)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-border pt-3">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-2">
                  Registration Details
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[11px] text-muted-foreground">Created</p>
                    <p className="text-[12px] font-medium">{formatDate(patient.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground">Form</p>
                    <p className="text-[12px] font-medium truncate">{patient.formName || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {!isLoading && !error && !patient && patientId && (
            <div className="text-center py-8 text-muted-foreground text-[12px]">
              Patient not found
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-background">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} className="flex-1">
              Close
            </Button>
            <ButtonWithIcon
              variant="primary"
              size="sm"
              icon={ExternalLink}
              onClick={handleViewFullProfile}
              disabled={!patientId}
              className="flex-1"
            >
              View Profile
            </ButtonWithIcon>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function PatientQuickViewSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <AppSkeleton className="w-12 h-12 rounded-full" />
        <div className="flex-1 space-y-2">
          <AppSkeleton className="h-4 w-32" />
          <AppSkeleton className="h-3 w-20" />
          <AppSkeleton className="h-5 w-16 rounded-full" />
        </div>
      </div>
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-start gap-3">
            <AppSkeleton className="w-8 h-8 rounded-lg" />
            <div className="flex-1 space-y-1">
              <AppSkeleton className="h-3 w-16" />
              <AppSkeleton className="h-4 w-28" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

PatientQuickView.displayName = 'PatientQuickView';
export default PatientQuickView;
